/**
 * ENG-10503 — lift the REMEDY clause out of a usage banner, verbatim.
 *
 * ## Why this exists
 *
 * The org-wide spend banner that took alyve dark (ENG-10474) reads:
 *
 *     You've hit your org's monthly spend limit · run /usage-credits to ask your
 *     admin for a higher limit · your session limit resets 2:10am (UTC)
 *
 * The middle clause is a SELF-SERVICE fix: the customer could have raised the
 * limit in a minute. ENG-10497's customer notice already renders a
 * `banner_remedy` verbatim when an alert carries one, but nothing persisted the
 * clause, so the notice could only say "an admin usually has to raise the
 * limit". This module is the first link of that chain.
 *
 * ## Why a separate module and not `banner-parser.ts`
 *
 * ENG-10503's acceptance says `banner-parser.ts` is untouched. That was written
 * to avoid colliding with ENG-10474's in-flight parser change, and it is kept
 * here anyway: the parser's job is to classify a ceiling, and every other
 * consumer of it (the rate-limit classifier, the usage monitor) would pay for a
 * change it does not need.
 *
 * ## Two rules this cannot break
 *
 * 1. **Never synthesise.** A banner with no remedy clause yields null, not a
 *    plausible default. An invented `/usage-credits` is a confident guess about
 *    another product's UI, sent to a customer.
 * 2. **Untrusted display text.** It comes from a vendor banner read off a
 *    terminal pane and ends up in a customer Slack message, so it is
 *    length-bounded and stripped of anything that could change the message
 *    around it. `sanitizeBannerRemedy` is applied at the host AND again at the
 *    API, because the API cannot trust what an older or tampered host sends.
 */

import { parseUsageBanner } from './banner-parser.js';

/** Longest remedy we store or render. The real clause is ~57 characters. */
export const BANNER_REMEDY_MAX_LENGTH = 200;

export interface BannerRemedyMatch {
  /** The remedy clause, sanitised. */
  remedy: string;
  /**
   * The qualifier of the banner the remedy belongs to ("org's monthly spend",
   * "individual spend"), or null for a bare "hit your limit".
   *
   * Returned so the caller can pair the remedy with the SAME banner
   * `parseUsageBanner` chose. The two scan independently, and a pane tail can
   * hold an older spend banner (with a remedy) above a newer weekly banner
   * (without one); attaching the old remedy to the new ceiling would tell a
   * customer to raise a spend limit that is not what stopped them.
   */
  qualifier: string | null;
}

/**
 * A saturated banner whose `limit` is followed by a separated clause that is
 * itself followed, within one more clause, by `resets`.
 *
 * - The qualifier shape mirrors the parser's saturated and spend patterns
 *   (up to four tokens, apostrophes allowed), so the qualifier we return is
 *   the same string the parser exposes as `limitQualifier`.
 * - The remedy is `[^·]` and bounded: it cannot run past the next separator,
 *   and it may span the pane's mid-sentence line wrap.
 * - The lookahead requires a `resets` clause next, so arbitrary text between
 *   two middle dots in prose is not taken for a remedy.
 */
const REMEDY_PATTERN = new RegExp(
  "hit\\s+your\\s+(?:(?<qualifier>[a-z0-9'’]+(?:[\\s-]+[a-z0-9'’]+){0,3})\\s+)?limit" +
    '\\s*·\\s*(?<remedy>[^·]{1,' +
    String(BANNER_REMEDY_MAX_LENGTH * 2) +
    '}?)\\s*·(?=[^·]{0,80}?\\bresets\\b)',
  'giu',
);

/**
 * Characters removed outright before a remedy is stored or rendered:
 * - `<` `>`: Slack control sequences (`<!channel>`, `<@U…>`, `<url|text>`);
 * - backtick and `"`: would break out of the quoted, verbatim rendering;
 * - zero-width and bidi controls: invisible reordering of what the reader sees.
 */
const STRIP = /[<>`"​-‏‪-‮⁦-⁩﻿]/g;
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f-\u009f]/g;

/**
 * Reduce a remedy to safe, bounded, single-line display text, or null when
 * nothing is left. Accepts `unknown` because the API applies it to a request
 * body field it has not type-checked.
 */
export function sanitizeBannerRemedy(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const cleaned = raw
    .replace(CONTROL, ' ')
    .replace(STRIP, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned) return null;
  return cleaned.length > BANNER_REMEDY_MAX_LENGTH
    ? cleaned.slice(0, BANNER_REMEDY_MAX_LENGTH).trimEnd()
    : cleaned;
}

type LocatedRemedy = BannerRemedyMatch & { start: number; end: number };

/** Every remedy-bearing banner in `text`, oldest first, with where each match starts and ends. */
function remedyMatches(text: string): LocatedRemedy[] {
  const found: LocatedRemedy[] = [];
  if (!text) return found;
  const pattern = new RegExp(REMEDY_PATTERN.source, REMEDY_PATTERN.flags);
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index === pattern.lastIndex) pattern.lastIndex++;
    const remedy = sanitizeBannerRemedy(match.groups?.['remedy']);
    if (!remedy) continue;
    const qualifier = match.groups?.['qualifier']?.replace(/\s+/g, ' ').trim() || null;
    found.push({ remedy, qualifier, start: match.index, end: match.index + match[0].length });
  }
  return found;
}

/** The last remedy-bearing banner in `text`. */
function lastRemedyMatch(text: string): LocatedRemedy | null {
  const all = remedyMatches(text);
  return all.length > 0 ? all[all.length - 1]! : null;
}

/**
 * The remedy clause of the MOST RECENT remedy-bearing banner in `text`, or null.
 *
 * This alone says nothing about whether that banner is the one the parser
 * reported; use `bannerRemedyFor` to attach a remedy to an observation.
 */
export function extractBannerRemedy(text: string): BannerRemedyMatch | null {
  const found = lastRemedyMatch(text);
  return found ? { remedy: found.remedy, qualifier: found.qualifier } : null;
}

/**
 * The remedy for the banner `parseUsageBanner` chose, or null.
 *
 * Paired by OCCURRENCE, not just by qualifier (CodeRabbit on #6057). The parser
 * reports the latest VALID banner across all of its patterns, and applies rules
 * of its own (the trailing-prose guard, a parseable reset). Rather than copy
 * that grammar here, the parser itself is asked two questions:
 *
 *   1. Is there a valid banner from the remedy's banner onward? If not, the
 *      parser rejected that banner and chose an EARLIER one, which is not the
 *      banner this remedy belongs to.
 *   2. Is there any banner after the remedy clause? If so, a NEWER banner was
 *      chosen, and it carried no remedy of its own. Same qualifier or not, the
 *      older remedy must not ride along.
 *
 * Only when both say "this occurrence" is the qualifier compared, as a last
 * guard (case- and whitespace-insensitive).
 *
 * Candidates are tried NEWEST FIRST, not just the newest (CodeRabbit on #6057).
 * A remedy-shaped banner the parser rejects can sit after the valid one the
 * parser chose; looking only at the last match would drop the chosen banner's
 * own remedy. A rejected later banner does not fail question 2 for an earlier
 * candidate, because the parser finds nothing valid after it.
 */
export function bannerRemedyFor(
  text: string,
  observedQualifier: string | null | undefined,
  now: Date = new Date(),
): string | null {
  const norm = (q: string | null | undefined) => (q ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
  const candidates = remedyMatches(text);
  for (let i = candidates.length - 1; i >= 0; i--) {
    const found = candidates[i]!;
    if (parseUsageBanner(text.slice(found.start), now) === null) continue;
    // A valid banner after this remedy means a newer banner was chosen; every
    // older candidate is further from it, so none of them can pair either.
    if (parseUsageBanner(text.slice(found.end), now) !== null) return null;
    return norm(found.qualifier) === norm(observedQualifier) ? found.remedy : null;
  }
  return null;
}
